import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { BillingCycle, format, labels } from "components/shareable/UpgradeToProModal/utils";
import Sty, { Container, StyledLabelP, SymphonyTooltip } from "modules/Sty";
import { OrganizationStatus, PlaylistingCampaignInput, YouTubeCampaignData, CurrentBrand, OrganizationData } from "modules/Types";
import getSymbolFromCurrency from "currency-symbol-map";
import {
    CheckoutType,
    PRO_MONTHLY_PLAN_PRICE,
    TEAM_MONTHLY_PLAN_PRICE,
    TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE,
    PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
    TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
    ONE_OFF_CAMPAIGN_PRICE,
    DEFAULT_CURRENCY,
    AdPlatform,
    PlaybookType,
    PlanType,
    PlaybookStepName
} from "modules/Const";
import { getNumberWithDecimals } from "helpers/General";
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import DEPRECATED_useOrganization from 'Hooks/useOrganization';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import { clsx, formatNumberToDisplayType, showToast, styx } from 'modules/Utils';
import Colors from "modules/Colors";
import useUser from "Hooks/useUser";
import ButtonSymphony from "elements/ButtonSymphony";
import { getConvertedCurrency_api } from "api/campaignApi";
import PayPalButtonGroup from "pages/MarketingPage/Components/GoLiveCard/PaypalCheckoutOption";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { toastError } from "modules/Toast";
import { useHistory } from "react-router-dom";
import { CSSProperties } from "react";
import { track } from "analytics";
import useFacebookConnections from "Hooks/useFacebookConnections";
import CardSymphony from "elements/CardSymphony";
import debounce from 'lodash/debounce';
import SwitchSymphony, { SwitchType } from "elements/SwitchSymphony";
import Banner from "elements/Banner";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import CampaignCheckoutView from '../../elements/CampaignCheckoutView';
import UnifiedPaymentForm from 'elements/UnifiedPaymentForm';
import { ConfirmPaymentResponse, PaymentStatusCheckResponse } from "api/paymentsV2Api";
import useScreen from "Hooks/useScreen";
import LineItemsSummary from "components/shareable/LineItemsSummary";

type CurrencyPrices = {
    annualConverted: number,
    monthlyConverted: number,
    oneOffConverted: number
}

/**
 * Determines the unified payment flow for a campaign
 * Consolidates all payment logic into a single decision function
 */
const getUnifiedPaymentFlow = (
    currentBrand: CurrentBrand,
    organization: OrganizationData | undefined,
    adPlatform: string,
    playbookType: string,
    usesExpressSetup: boolean,
    selectedFacebookPage: any,
    selectedFacebookAdAccount: any
) => {

    // Pro accounts dont pay a fee for FB campaigns where they've connected their own ad account
    // Grandfathered organizations dont pay a fee for FB campaigns where they've connected their own ad account
    let isDirectCreateTier = currentBrand?.tier === PlanType.PRO || (organization?.metadata?.isGrandfathered);
    // 1. Pro users with their own ad account = NO PAYMENT (directCreate)
    if (isDirectCreateTier &&
        adPlatform === AdPlatform.META &&
        !usesExpressSetup &&
        selectedFacebookPage &&
        selectedFacebookAdAccount) {
        return { type: 'directCreate', requiresPayment: false, description: 'Pro Meta with own ad account' }
    }

    // 2. All other scenarios = UnifiedPaymentForm (backend determines playlisting vs automation fee)
    return { type: 'unifiedPayment', requiresPayment: true, description: 'Backend-determined pricing' }
}

const SubmitOrCheckoutButtonsView = ({
    overrideCampaignInput,
    style
}: {
    /** Used for old campaigns not supported in new Campaign store */
    overrideCampaignInput?:
    YouTubeCampaignData,
    style?: React.CSSProperties
}) => {
    const history = useHistory();
    const {
        currentBrand,
        isProBrand,
        getBrands,
        getShouldShowNewUserUpgradePrompt // Using the new selector
    } = useBrand() as BrandExistsState

    const { proFreeTrialRedeemed } = useUser()
    const { mobileView } = useScreen();
    const { organization } = useOrganizationV2();
    const {
        submitCampaign,
        campaignDisclaimer,
        isSubmittingCampaign,
        adPlatform,
        playbookType,
        isGeographyBucketsObjectValid,
        budget: campaignBuilderBudget,
        automationFeeData,
        isLoadingAutomationFee,
        getAutomationFee,
        campaignConfirmationChecked,
        setCampaignConfirmationChecked,
        getConfirmationDisclaimerText,
        isConfirmationRequired,
        usesExpressSetup,
        currentStep,
        getDataForSubmission
    } = useCampaignBuilder()

    // Get Facebook connections for Pro Meta direct create logic
    const { selectedFacebookPage, selectedFacebookAdAccount } = useFacebookConnections()

    // Unified payment flow determination
    const paymentFlow = getUnifiedPaymentFlow(
        currentBrand,
        organization,
        adPlatform || AdPlatform.META,
        playbookType || PlaybookType.INCREASE_VIDEO_VIEWS,
        usesExpressSetup(),
        selectedFacebookPage,
        selectedFacebookAdAccount
    )

    // New upgrade check based on the selector from useBrand
    const requiresUpgradeToCheckout = getShouldShowNewUserUpgradePrompt();

    // Extract the most repetitive logic
    const isConfirmationBlocking = isConfirmationRequired() && !campaignConfirmationChecked

    const [currencyPrices, setCurrencyPrices] = useState<CurrencyPrices>({
        annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
        monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
        oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
    })

    const [billingCycle, setBillingCycle] = useState<BillingCycle>(BillingCycle.MONTHLY)

    const currencyCode = currentBrand.currency?.code ?? DEFAULT_CURRENCY

    const freeTrialOver = proFreeTrialRedeemed()
    // get the currency codes
    const currencySymbol = getSymbolFromCurrency(currencyCode)

    // for non-Pro users, we show the option to run a one-off campaign or upgrade to Pro
    const [selectedTab, setSelectedTab] = useState<CheckoutType>(CheckoutType.ONE_OFF_CAMPAIGN)

    const paypalOrderDetails = useRef<any>(); // Reference to store PayPal order details

    // Create debounced version of automation fee calculation
    const debouncedGetAutomationFee = useCallback(
        debounce(() => {
            getAutomationFee();
        }, 400), // Shorter debounce since this appears later in the flow
        [] // getAutomationFee is stable from Zustand store
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedGetAutomationFee.cancel();
        };
    }, []); // debouncedGetAutomationFee is stable, no need to depend on it


    // Use campaign builder budget (video view campaigns now set this directly)
    const budget = campaignBuilderBudget
    // this is used ONLY for international currencies - specifically when a user selects an ad account with an international currency, and
    // we need to convert the "Upgrade to Pro" price to the international currency
    const getPricesValues = async () => {
        let baseMonthly;
        let baseAnnual;
        let oneOffCampaign;

        oneOffCampaign = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: ONE_OFF_CAMPAIGN_PRICE })
    }

    // Effect to fetch automation fees when budget or platform changes
    useEffect(() => {
        // Calculate fees for:
        // - One-off campaigns (always)
        // - Pro brands on YouTube/TikTok (still have automation fees)
        // - Non-pro brands (always)
        const shouldCalculateFees = budget && adPlatform && (
            selectedTab === CheckoutType.ONE_OFF_CAMPAIGN ||
            !isProBrand || // This will effectively be !isPaidBrand after full refactor
            (isProBrand && (adPlatform === AdPlatform.YOUTUBE || adPlatform === AdPlatform.TIKTOK))
        );

        if (shouldCalculateFees) {
            debouncedGetAutomationFee();
        }
    }, [budget, adPlatform, selectedTab, isProBrand]); // debouncedGetAutomationFee is stable

    useEffect(() => {
        if (currencyCode !== DEFAULT_CURRENCY) {

            getPricesValues()
        } else {
            // TODO: remove this else statement when we fix the way we switch from brand to brand, right now it keeps some values unchanged
            if (currencyPrices.monthlyConverted !== PRO_MONTHLY_PLAN_PRICE) {
                setCurrencyPrices({
                    annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
                    monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
                    oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
                })
            }

        }
    }, [currencyCode])

    /** 
     * Creates a PayPal order through the campaign checkout API
     * This is used for both Pro users and one-off campaigns
     * @returns Promise resolving to the PayPal order ID
     */
    // const createPaypalOrder = async () => {
    //     try {
    //         if (isSubmittingCampaign) return '';

    //         // Set loading state
    //         // isSubmittingCampaign is already managed by the useCampaign hook

    //         // Determine which checkout type to use based on the selected tab
    //         const checkoutType = selectedTab === CheckoutType.ONE_OFF_CAMPAIGN ?
    //             'oneOffCheckout' : 'upgradePlanCheckout';

    //         // Make API call to create checkout session
    //         // We use the same submitCampaign function for both card and PayPal
    //         const response = await submitCampaign({
    //             submitType: checkoutType,
    //             recurring_interval: billingCycle,
    //             overrideCampaignInput
    //         });

    //         if (!response) {
    //             throw new Error('Failed to create PayPal order');
    //         }

    //         // Store order details for use in onApprove callback
    //         paypalOrderDetails.current = {
    //             orderId: response?.paypalOrderId || 'mock-order-id', // PayPal order ID from API
    //             campaignId: response?.campaign?.id || response?.id   // Campaign ID to redirect to later
    //         };

    //         return paypalOrderDetails.current.orderId;
    //     } catch (error) {
    //         console.error('Error creating PayPal order:', error);
    //         toastError('There was an error creating your PayPal order. Please try again.');
    //         return '';
    //     }
    // }

    /**
     * Handles cancellation of PayPal checkout flow
     */
    const onCancelPayPalOrder = () => {
        // Reset loading state if needed
        // Track cancellation for analytics if necessary
        console.log('PayPal checkout cancelled');
        return Promise.resolve();
    }

    /**
     * Handles errors in the PayPal checkout flow
     * @param error The error object from PayPal
     */
    const onErrorPayPalOrder = () => {
        console.error('PayPal error occurred');
        toastError('There was an error processing your PayPal payment. Please try again.');
        return Promise.resolve();
    }

    /**
     * Handles successful PayPal order approval
     * Redirects to the campaign submission success page
     */
    const onPayPalOrderApprove = async () => {
        try {
            if (!paypalOrderDetails.current) {
                throw new Error('PayPal order details not found');
            }

            const { campaignId } = paypalOrderDetails.current;

            if (campaignId) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${campaignId}`);
            } else {
                throw new Error('Campaign ID not found in PayPal order details');
            }

            return Promise.resolve();
        } catch (error) {
            console.error('Error processing PayPal approval:', error);
            toastError('There was an error processing your payment. Please contact support.');
            return Promise.resolve();
        }
    }

    /**
     * Renders the confirmation checkbox for campaigns that require user confirmation
     * @param wrapInBanner Whether to wrap the switch in a purple Banner component
     * @param additionalStyle Additional styles to apply to the switch
     */
    const renderConfirmation = () => {
        if (!isConfirmationRequired()) {
            return null;
        }

        const switchComponent = (
            <SwitchSymphony
                className="campaignConfirmationCheckbox"
                type={SwitchType.SWITCH}
                checked={campaignConfirmationChecked}
                onChange={(checked) => setCampaignConfirmationChecked(checked)}
                label={getConfirmationDisclaimerText()}
            />
        );

        return (
            <Banner color="purple" className="confirmationBanner">
                {switchComponent}
            </Banner>
        );
    }

    /**
     * Renders the PayPal button component
     * @param allowNonPro Whether to allow non-Pro users to use PayPal (for one-off campaigns)
     */
    const renderPaypalButton = (allowNonPro: boolean = false) => {
        // PayPal functionality removed - now handled by UnifiedPaymentForm
        return null
        // return (
        //     <PayPalButtonGroup
        //         createOrder={createPaypalOrder}
        //         onCancel={onCancelPayPalOrder}
        //         onError={onErrorPayPalOrder}
        //         onApprove={onPayPalOrderApprove}
        //         className="w-full"
        //         currencyCode={currencyCode}
        //         style={{ marginTop: 8 }}
        //         allowWithoutPro={allowNonPro}
        //     />
        // )
    }

    const handleClickSubmitButton = async (submitType: 'directCreate') => {
        try {
            let res = await submitCampaign({ submitType, recurring_interval: billingCycle, overrideCampaignInput });

            // Handle redirected responses
            if (res?.redirected) {
                // Already redirected to Stripe, nothing more to do
                return;
            }

            track("Submit Campaign", {
                adPlatform,
                playbookType,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
            })

            // For direct creation or when the user is already on a paid plan (no URL redirect needed)
            if (res?.campaign?.id) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${res.campaign.id}`);
            } else if (submitType === 'directCreate' && res?.id) {
                // Handle case where response directly contains the campaign ID
                history.push(`/marketing2/submission/${res.id}`);
            }
        } catch (error) {
            console.error('error: ', error);
            toastError('There was an error submitting your campaign. Please try again.');
        }
    }

    /**
     * Renders the view for Pro users, showing submission UI
     */
    const renderProView = () => {

        let mainComponent = (
            <>
                {/* Confirmation checkbox for TikTok/YouTube campaigns */}
                {renderConfirmation()}

                <div className="campaignDisclaimer" style={styx(Sty.text16grey, { textAlign: 'center', marginBottom: 16 })} >
                    {campaignDisclaimer}
                </div>
                <ButtonSymphony
                    className="submitCampaignButton1"
                    onClick={() => handleClickSubmitButton('directCreate')}
                    width="100%"
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || isConfirmationBlocking}
                    isLoading={isSubmittingCampaign}
                >
                    Submit Campaign
                </ButtonSymphony>
            </>
        )

        return (
            <div className={clsx(`submitCampaignOrUpgradeComponent`)} style={style}>
                {mainComponent}
            </div>
        );
    }

    /**
     * Checks if the checkout view can be displayed based on campaign type and required data
     */
    const canDisplayCheckoutView = () => {
        // For playlisting campaigns, we need specific data to be present
        if (playbookType === PlaybookType.GET_PLAYLISTED) {
            const campaignBuilderState = useCampaignBuilder.getState();
            return !!(
                campaignBuilderState.selectedSongToPromote &&
                campaignBuilderState.selectedPlaylistingTier
            );
        }

        // For other campaign types, we can display if we have basic requirements
        return !!(playbookType && adPlatform);
    }

    /**
     * Renders the view for direct campaign charging (starter/growth/team_v2 plans)
     */
    const renderDirectChargeView = () => {
        // Guard against premature rendering for campaigns that need additional setup
        if (!canDisplayCheckoutView()) {
            return (
                <div className={clsx(`submitCampaignOrUpgradeComponent directChargeView`)} style={style}>
                    <div style={{ padding: 16, textAlign: 'center', color: '#666' }}>
                        {playbookType === PlaybookType.GET_PLAYLISTED
                            ? 'Please select a song and tier to continue with checkout.'
                            : 'Please complete campaign setup to continue with checkout.'
                        }
                    </div>
                </div>
            );
        }

        let transformedCampaignInput = overrideCampaignInput || getDataForSubmission()

        // Only override budget if it's not already set (for non-playlisting campaigns)
        if (typeof transformedCampaignInput === 'object' && transformedCampaignInput !== null) {
            transformedCampaignInput.budget = Number(campaignBuilderBudget)
        } else {
            console.error('getDataForSubmission returned invalid data:', transformedCampaignInput)
            return null // or handle error appropriately
        }
        const paymentData = {
            type: 'campaign' as const,
            planType: currentBrand?.tier as PlanType,
            campaignInput: transformedCampaignInput,
            usesExpressSetup: 'usesExpressSetup' in transformedCampaignInput ? transformedCampaignInput.usesExpressSetup : false,
        }

        const handlePaymentSuccess = (result: PaymentStatusCheckResponse) => {
            track("Submit Campaign", {
                adPlatform,
                playbookType,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
            })

            // The campaignId now comes from the payment status response
            // after the webhook has processed the payment and finalized the campaign
            if (result.campaignId) {
                history.push(`/marketing2/submission/${result.campaignId}`);
            } else {
                console.warn('Payment succeeded but no campaignId returned:', result);
                // Fallback: could redirect to campaigns list or show success message
            }
        }

        const handlePaymentError = (error: string) => {
            console.error('Campaign payment error:', error);
            toastError('There was an error processing your payment. Please try again.');
        }

        const shouldLoadIntent = currentStep === PlaybookStepName.FINALIZATION

        return (
            <div className={clsx(`submitCampaignOrUpgradeComponent directChargeView`)} style={style}>
                <div style={{ marginBottom: 16 }}>
                    <CardSymphony
                        className="directChargeCampaignCard"
                        isNoPadding
                        title="Checkout + Submit"
                        subtitle="Your campaign will be charged directly and submitted for processing."
                    />
                </div>

                <LineItemsSummary
                    lineItems={automationFeeData?.lineItems || []}
                    totalAmount={automationFeeData?.totalCost || 0}
                    showTitle={false}
                    isLoading={isLoadingAutomationFee}
                    style={{ marginBottom: 16 }}
                />

                {/* Confirmation checkbox for TikTok/YouTube campaigns */}
                {renderConfirmation()}

                <div className="campaignDisclaimer" style={styx(Sty.text16grey, { textAlign: 'center', marginBottom: 16 })} >
                    {campaignDisclaimer}
                </div>

                <UnifiedPaymentForm
                    shouldLoadIntent={shouldLoadIntent}
                    paymentData={paymentData}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    submitButtonDisabled={isConfirmationRequired() ? !campaignConfirmationChecked : false}
                    submitButtonText={isLoadingAutomationFee ? 'Calculating fees...' : 'Checkout + Submit'}
                />
            </div>
        );
    }

    // renderOneOffCampaignView removed - replaced by unified payment flow using UnifiedPaymentForm

    // REMOVED renderUpgradePlanView() function as per spec (lines 678-788)
    const onPaidCampaignCheckout = async (result: PaymentStatusCheckResponse) => {
        console.log('result', result)
        if (result.completed) {
            const campaignId = result.campaignId

            console.log('campaignId', campaignId)
            if (!campaignId) {
                showToast({
                    message: "Payment succeeded but campaign creation failed. Please contact support.",
                    error: true,
                    mobile: mobileView
                });
            } else {
                history.push(`/marketing2/submission/${campaignId}`);
            }
            await getBrands(true)
        } else {
            showToast({
                message: result.error || "Payment failed.",
                error: true,
                mobile: mobileView
            });
        }
    }
    // Check if user needs to upgrade before checkout
    if (requiresUpgradeToCheckout && playbookType !== PlaybookType.GET_PLAYLISTED) {
        return (
            <div
                id="campaignSubmitButtonsContainer"
                className="campaignSubmitButtonsContainer submitOrCheckoutButtonsView"
                data-testid="campaign-submit-buttons"
            >
                <CampaignCheckoutView
                    className="campaignPlanSelection"
                    style={style}
                    onPaid={onPaidCampaignCheckout}
                    isCampaignBundle={true}
                    overrideCampaignInput={overrideCampaignInput}
                />
            </div>
        );
    }

    // UNIFIED PAYMENT FLOW LOGIC
    let viewToRender;

    if (paymentFlow.type === 'directCreate') {
        // Pro users with their own Meta ad account - no payment required
        viewToRender = renderProView();
    } else if (paymentFlow.requiresPayment) {
        // All other scenarios use UnifiedPaymentForm via renderDirectChargeView
        viewToRender = renderDirectChargeView();
    } else {
        // Fallback (shouldn't happen with current logic)
        console.warn('Unexpected payment flow state:', paymentFlow);
        viewToRender = renderDirectChargeView();
    }

    return (
        <div
            id="campaignSubmitButtonsContainer"
            className="campaignSubmitButtonsContainer submitOrCheckoutButtonsView"
            data-testid="campaign-submit-buttons"
        >
            {viewToRender}
        </div>
    );
}

export default SubmitOrCheckoutButtonsView